'use client'

import { Check, MapPin } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import {
  getPricingCountryMeta,
  SUPPORTED_PRICING_COUNTRIES,
  useLocalizedPricing,
} from '@/hooks/use-localized-pricing'

type CountrySelectorProps = {
  variant?: 'desktop' | 'mobile'
  className?: string
}

export function CountrySelector({
  variant = 'desktop',
  className,
}: CountrySelectorProps) {
  const {
    countryCode,
    manualCountryCode,
    displayCurrency,
    clearManualCountry,
    setManualCountry,
  } = useLocalizedPricing()

  const activeCountry = getPricingCountryMeta(countryCode)
  const autoLabel = countryCode
    ? `Auto (${activeCountry.label})`
    : 'Auto (EUR)'

  if (variant === 'mobile') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'inline-flex h-8 items-center gap-1 rounded-full bg-slate-50 px-2 text-[11px] font-medium text-slate-700 hover:bg-slate-100 transition-colors',
              className,
            )}
          >
            <MapPin className="h-3.5 w-3.5" />
            <span>{activeCountry.code || 'EUR'}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[220px] bg-white">
          <DropdownMenuItem
            onClick={clearManualCountry}
            className="flex items-center justify-between gap-3 !p-2"
          >
            <div className="flex flex-col">
              <span className="text-sm font-medium text-slate-900">{autoLabel}</span>
              <span className="text-xs text-slate-500">Use detected country and currency</span>
            </div>
            <Check
              className={cn(
                'h-4 w-4 text-[#4568DC]',
                manualCountryCode === null ? 'opacity-100' : 'opacity-0',
            )}
          />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
          <div className="max-h-[280px] overflow-y-auto pr-1">
            {SUPPORTED_PRICING_COUNTRIES.map((country) => (
              <DropdownMenuItem
                key={country.code}
                onClick={() => setManualCountry(country.code)}
                className="flex items-center justify-between gap-3 !p-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-slate-900">{country.label}</span>
                  <span className="text-xs text-slate-500">
                    {country.code} · {country.currency}
                  </span>
                </div>
                <Check
                  className={cn(
                    'h-4 w-4 text-[#4568DC]',
                    manualCountryCode === country.code ? 'opacity-100' : 'opacity-0',
                  )}
                />
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors',
            className,
          )}
        >
          <MapPin className="h-3.5 w-3.5 text-slate-500" />
          <span>{activeCountry.code || 'EUR'}</span>
          <span className="text-slate-400">{displayCurrency}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[260px] bg-white">
        <DropdownMenuItem
          onClick={clearManualCountry}
          className="flex items-center justify-between gap-3 !p-2"
        >
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-900">{autoLabel}</span>
            <span className="text-xs text-slate-500">Use detected country and currency</span>
          </div>
          <Check
            className={cn(
              'h-4 w-4 text-[#4568DC]',
              manualCountryCode === null ? 'opacity-100' : 'opacity-0',
            )}
          />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="max-h-[320px] overflow-y-auto pr-1">
          {SUPPORTED_PRICING_COUNTRIES.map((country) => (
            <DropdownMenuItem
              key={country.code}
              onClick={() => setManualCountry(country.code)}
              className="flex items-center justify-between gap-3 !p-2"
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-slate-900">{country.label}</span>
                <span className="text-xs text-slate-500">
                  {country.code} · {country.currency}
                </span>
              </div>
              <Check
                className={cn(
                  'h-4 w-4 text-[#4568DC]',
                  manualCountryCode === country.code ? 'opacity-100' : 'opacity-0',
                )}
              />
            </DropdownMenuItem>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
